import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, AlertTriangle, Shield, Clock, FileText, Send } from "lucide-react";
import EntityLink from "../components/shared/EntityLink";
import EntityNotes from "../components/shared/EntityNotes";
import EntityComms from "../components/shared/EntityComms";
import { Entity360Shell } from "../components/shared/OSComponents";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  critical: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-blue-100 text-blue-700",
};

export default function Exception360Page({ exceptionId, onBack, breadcrumbs = [] }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/entity/exception/${exceptionId}`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [exceptionId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]" data-testid="exception-not-found">Exception not found</div>;

  const { entity, kpis: kpiData, relationships, timeline, permissions } = data;
  const crumbs = [...breadcrumbs, { type: "exception", id: exceptionId, label: `Exception: ${entity.type || exceptionId.slice(0, 8)}` }];

  const relatedEntities = [];
  if (relationships.source_entity) {
    relatedEntities.push({
      type: relationships.source_entity.entity_type,
      id: relationships.source_entity.entity_id,
      title: relationships.source_entity.label,
      subtitle: relationships.source_entity.sublabel,
    });
  }
  (relationships.related_exceptions || []).forEach(e => {
    relatedEntities.push({ type: "exception", id: e.entity_id, title: e.label, subtitle: e.sublabel });
  });

  const sevStyle = SEVERITY_STYLES[kpiData.severity] || "bg-gray-100 text-gray-600";

  const kpis = [
    { label: "Type", value: (kpiData.type || "—").replace(/_/g, " "), icon: AlertTriangle },
    { label: "Severity", value: kpiData.severity || "—", icon: Shield, accent: kpiData.severity === "critical" },
    { label: "Status", value: (kpiData.status || "—").replace(/_/g, " "), icon: Clock },
    { label: "Entity Type", value: (kpiData.entity_type || "—").replace(/_/g, " "), icon: FileText },
  ];

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "related", label: `Related (${relatedEntities.length})` },
    { key: "comms", label: "Communications" },
    { key: "notes", label: "Notes" },
  ];

  return (
    <Entity360Shell type="exception" title={`Exception: ${(entity.type || "").replace(/_/g, " ")}`}
      subtitle={entity.description?.slice(0, 100) || ""} status={entity.status}
      badges={[{ label: kpiData.severity?.toUpperCase() || "—", className: sevStyle }]}
      kpis={kpis} tabs={tabs} activeTab={tab} onTabChange={setTab}
      onBack={onBack} breadcrumbs={crumbs} onNavigate={navigateToEntity} relatedEntities={relatedEntities}>

      {tab === "overview" && (
        <div className="space-y-4" data-testid="exception-overview">
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
            <h3 className="text-xs font-bold text-[#222] mb-3">Exception Details</h3>
            <div className="space-y-2 text-xs">
              <div><span className="text-[#999]">Type:</span> <span className="font-medium text-[#333]">{entity.type?.replace(/_/g, " ") || "—"}</span></div>
              <div><span className="text-[#999]">Description:</span> <span className="font-medium text-[#333]">{entity.description || "—"}</span></div>
              <div><span className="text-[#999]">Severity:</span> <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sevStyle}`}>{entity.severity || "—"}</span></div>
              <div><span className="text-[#999]">Status:</span> <span className="font-medium text-[#333]">{entity.status?.replace(/_/g, " ") || "—"}</span></div>
              <div><span className="text-[#999]">Created:</span> <span className="font-medium text-[#333]">{entity.created_at ? new Date(entity.created_at).toLocaleString() : "—"}</span></div>
            </div>
          </div>

          {entity.resolution && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <h3 className="text-xs font-bold text-emerald-800 mb-2">Resolution</h3>
              <p className="text-xs text-emerald-700">{entity.resolution}</p>
              {entity.resolved_at && <p className="text-[10px] text-emerald-600 mt-1">Resolved: {new Date(entity.resolved_at).toLocaleString()}</p>}
            </div>
          )}

          {relationships.source_entity && (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
              <h3 className="text-xs font-bold text-[#222] mb-3">Source Entity</h3>
              <EntityLink type={relationships.source_entity.entity_type} id={relationships.source_entity.entity_id}
                label={relationships.source_entity.label} sublabel={relationships.source_entity.sublabel} onClick={navigateToEntity} />
            </div>
          )}
        </div>
      )}

      {tab === "related" && (
        <div className="space-y-2" data-testid="exception-related">
          {relatedEntities.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#888]">No related entities</div>
          ) : relatedEntities.map((e, i) => (
            <div key={`${e.type}-${e.id}-${i}`} className="rounded-lg border border-[#e8e0d0] bg-white p-3 flex items-center justify-between hover:shadow-sm transition-shadow">
              <EntityLink type={e.type} id={e.id} label={e.title} sublabel={e.subtitle} onClick={navigateToEntity} />
              <span className="text-[10px] text-[#999] capitalize">{e.type}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "comms" && (
        <EntityComms entityType="exception" entityId={exceptionId} />
      )}

      {tab === "notes" && (
        <EntityNotes entityType="exception" entityId={exceptionId} onNavigate={navigateToEntity} />
      )}
    </Entity360Shell>
  );
}
